/**
 * Shared Zod schemas for the scheduler / dispatcher mutating endpoints.
 *
 * Used by the admin CancelRideDialog (client-side validation through
 * react-hook-form's `zodResolver`) and by the matching Fastify routes
 * (server-side body validation through `parseBody`). The richer dialogs
 * (OrderDialog, RideDialog) keep their existing client-side state
 * machines; their server routes still benefit from the shared schema.
 */
import { z } from 'zod';
import { RIDE_CANCEL_REASONS } from '../../constants/index.js';
import { orderSnapshotRequestShape } from '../responses/rides.js';

/**
 * Body schema for `POST /api/rides/:id/cancel`.
 *
 * - `reason` must be one of the catalog values. The catalog lives in
 *   `RIDE_CANCEL_REASONS` so server, admin SPA, and mobile driver app
 *   all agree on the wire shape.
 * - `reasonText` is required (non-empty after trim) only when
 *   `reason === OTHER`. The route also enforces this, but the schema
 *   captures it in one place — `.superRefine` lets the client get the
 *   error against the right field path so the dialog can highlight the
 *   textarea instead of toasting a generic message.
 */
export const cancelRideSchema = z
  .object({
    reason: z.enum(
      Object.values(RIDE_CANCEL_REASONS) as [string, ...string[]],
      { message: 'fleet.cancelRideDialog.errors.reasonRequired' },
    ),
    reasonText: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.reason !== RIDE_CANCEL_REASONS.OTHER) return;
    if (data.reasonText && data.reasonText.trim().length > 0) return;
    ctx.addIssue({
      code: 'custom',
      path: ['reasonText'],
      message: 'fleet.cancelRideDialog.errors.reasonTextRequired',
    });
  });

export type CancelRideInput = z.infer<typeof cancelRideSchema>;

// ── Ride POST/PATCH bodies ─────────────────────────────────────────────────

/**
 * Body shape for `POST /api/rides` and `PATCH /api/rides/:id`. The route is
 * intentionally permissive — every field is optional and the handler does
 * its own per-field fallbacks (e.g. unset values → null in the DB). The
 * schema's job is to catch wrong-type values (e.g. a number where a string
 * is expected) at the boundary so the route doesn't see garbage. Business
 * rules (vehicle/driver matching, override flow, audit emission) stay in
 * the route.
 *
 * `waypoints` carries the entire route as one ordered array; the route
 * enforces `length >= 2` (every ride has a pickup AND a dropoff). On
 * POST the field is required; on PATCH it is optional (omit to keep the
 * existing waypoints, send an array to replace them wholesale).
 *
 * Date fields accept ISO 8601 strings (the route wraps them in `new Date()`).
 * `routeGeometry` is forwarded to Prisma verbatim (Json column), so we
 * accept `unknown`.
 */
export const rideBodySchema = z.object({
  // Order/passenger snapshot fields shared with `recurringTripBodySchema` via
  // `orderSnapshotRequestShape`: passenger names + phone + linked-user id,
  // vehicleTypeRequiredId, waypoints (index 0 = pickup, last = dropoff),
  // paymentOption, extraCategories, personCount, fare, notes, and the
  // dispatcher-set `type` classification. All permissive (nullable+optional);
  // the route does its own per-field fallbacks.
  ...orderSnapshotRequestShape,

  // Vehicle + driver assignment (Either Driver.id or User.id; route resolves).
  fleetVehicleId:        z.string().nullable().optional(),
  driverId:              z.string().nullable().optional(),

  // Scheduling. ISO 8601 strings — route wraps in `new Date()`.
  scheduledStart: z.string().datetime({ offset: true }).nullable().optional(),
  scheduledEnd:   z.string().datetime({ offset: true }).nullable().optional(),
  // Planned pickup time of the round-trip return leg. Stored on the outbound
  // order so the round-trip intent persists while it is still unassigned.
  returnDatetime: z.string().datetime({ offset: true }).nullable().optional(),

  // Routing
  distance:      z.number().nullable().optional(),
  duration:      z.number().nullable().optional(),
  // GeoJSON LineString — passed through to Prisma verbatim.
  routeGeometry: z.unknown().optional(),

  // Misc
  declinable:      z.boolean().optional(),
  status:          z.string().optional(),
});

export type RideBodyInput = z.infer<typeof rideBodySchema>;
